"use client"

import React, { useState, useMemo, useEffect, useRef } from "react"
import { Input } from "@/shared/components/ui/input"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { cn } from "@/shared/lib/utils"
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/shared/components/ui/popover"

export interface SearchOption {
  value: string
  label: string
}

interface ReusableSearchInputProps {
  options: SearchOption[]
  selectedValue?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  showSelectedCard?: boolean
  loading?: boolean
}

function ReusableSearchInput({
  options,
  selectedValue = "",
  onValueChange,
  placeholder = "Search...",
  emptyText = "No results found.",
  disabled = false,
  className,
  showSelectedCard = false,
  loading = false,
}: ReusableSearchInputProps) {
  // Core state - only what we absolutely need
  const [inputValue, setInputValue] = useState("")
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Filter options based on input value
  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return options
    
    const searchLower = inputValue.toLowerCase()
    return options.filter(option =>
      option.label.toLowerCase().includes(searchLower)
    )
  }, [options, inputValue])

  // Find selected option for display
  const selectedOption = useMemo(() => {
    if (!selectedValue) return null
    return options.find(option => option.value === selectedValue) || null
  }, [selectedValue, options])

  // Initialize input value from selectedValue prop
  useEffect(() => {
    if (selectedValue && selectedOption) {
      setInputValue(selectedOption.label)
    } else if (!selectedValue) {
      setInputValue("")
    }
  }, [selectedValue, selectedOption])

  // Handle input change
  const handleInputChange = (value: string) => {
    setInputValue(value)
    // Open dropdown when user starts typing
    if (value.trim() && !isDropdownOpen) {
      setIsDropdownOpen(true)
    }
  }

  // Handle input focus - open dropdown
  const handleInputFocus = () => {
    setIsDropdownOpen(true)
  }

  // Handle input click - open dropdown
  const handleInputClick = () => {
    setIsDropdownOpen(true)
  }

  // Handle option selection from dropdown
  const handleOptionSelect = (option: SearchOption) => {
    setInputValue(option.label)
    setIsDropdownOpen(false)
    
    // Call the value change callback
    onValueChange?.(option.value)
  }

  // Handle clear
  const handleClear = () => {
    setInputValue("")
    setIsDropdownOpen(false)
    
    // Call the value change callback
    onValueChange?.("")
  }

  // Handle key down
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsDropdownOpen(false)
      inputRef.current?.blur()
    }
  }

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current) return
      
      const target = event.target as Node
      const container = containerRef.current
      
      // Check if the click is outside the container
      if (!container.contains(target)) {
        // Additional check: if clicking on scrollbar or within popover content, don't close
        const targetElement = target as Element
        
        // Check if clicking on scrollbar elements
        if (targetElement && (
          targetElement.classList.contains('scrollbar-thumb') ||
          targetElement.classList.contains('scrollbar-track') ||
          targetElement.closest('[data-radix-scroll-area-viewport]') ||
          targetElement.closest('[data-radix-popper-content-wrapper]')
        )) {
          return // Don't close if clicking on scrollbar or popover content
        }
        
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  // Handle Popover open change
  const handlePopoverOpenChange = (open: boolean) => {
    setIsDropdownOpen(open)
  }

  // Show selected card if enabled and option is selected
  if (showSelectedCard && selectedOption) {
    return (
      <div className={cn("w-full", className)}>
        <div className="flex items-center justify-between p-3 border rounded bg-background">
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">
              {selectedOption.label}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="hover:bg-accent rounded p-1 transition-colors"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <Popover 
        open={isDropdownOpen} 
        onOpenChange={handlePopoverOpenChange}
      >
        {/* Use PopoverAnchor instead of PopoverTrigger for the input area
            This prevents Radix from automatically toggling the popover on click */}
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => handleInputChange((e.target as HTMLInputElement).value)}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              onClick={handleInputClick}
              placeholder={placeholder}
              disabled={disabled || loading}
              className="h-14 pr-20"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {selectedValue && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleClear()
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation()
                  }}
                  className="hover:bg-accent rounded p-1 transition-colors"
                  tabIndex={-1}
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
              {!selectedValue && (
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="hover:bg-accent rounded p-1 transition-colors"
                  tabIndex={-1}
                >
                  <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
        </PopoverAnchor>
        <PopoverContent 
          className="p-0 w-[var(--radix-popover-trigger-width)]" 
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="max-h-[300px] overflow-y-auto">
            <div className="p-1">
              {loading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading...
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {inputValue.trim() ? "No results found matching your search." : emptyText}
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleOptionSelect(option)}
                    className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <Check className={cn(
                      "mr-2 h-4 w-4",
                      selectedValue === option.value ? "opacity-100" : "opacity-0"
                    )} />
                    <span className="truncate">{option.label}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default ReusableSearchInput

